import { Component, NgZone, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';

import { environment } from '../../../environments/environment';
import { AccountService } from '../../services/account.service';
import { BrandService } from '../../core/services/brand.service';
import { TokenStorageService } from '../../core/services/token-storage.service';
import { PortalFooterComponent } from '../../layout/portal-footer.component';

declare const grecaptcha: {
  ready(callback: () => void): void;
  execute(siteKey: string, options: { action: string }): Promise<string>;
};

const RECAPTCHA_SCRIPT_URL = `https://www.google.com/recaptcha/api.js?render=${environment.recaptchaSiteKey}`;

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, PortalFooterComponent],
  templateUrl: './login.component.html',
  styleUrl: './login.component.scss',
})
export class LoginComponent implements OnInit {
  protected brandService = inject(BrandService);
  private fb = inject(FormBuilder);
  private accountService = inject(AccountService);
  private tokenStorage = inject(TokenStorageService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private zone = inject(NgZone);

  loginForm: FormGroup = this.fb.group({
    domain: ['', Validators.required],
    zipCode: ['', Validators.required],
    phoneNumber: ['', Validators.required],
  });

  errorMessage = '';
  loading = false;

  ngOnInit(): void {
    if (!document.querySelector(`script[src="${RECAPTCHA_SCRIPT_URL}"]`)) {
      const script = document.createElement('script');
      script.src = RECAPTCHA_SCRIPT_URL;
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    }
  }

  onSubmit(): void {
    if (this.loginForm.invalid) {
      this.loginForm.markAllAsTouched();
      return;
    }

    if (typeof grecaptcha === 'undefined') {
      this.errorMessage = 'Unable to verify your request. Please refresh the page and try again.';
      return;
    }

    this.loading = true;
    this.errorMessage = '';

    grecaptcha.ready(() => {
      grecaptcha.execute(environment.recaptchaSiteKey, { action: 'login' }).then((recaptchaToken) => {
        this.zone.run(() => this.authenticate(recaptchaToken));
      });
    });
  }

  private authenticate(recaptchaToken: string): void {
    this.accountService.authenticate({ ...this.loginForm.value, recaptchaToken }).subscribe({
      next: (response) => {
        this.tokenStorage.storeToken(response.token);
        this.router.navigateByUrl(this.route.snapshot.queryParamMap.get('returnUrl') ?? '/bill-pay');
      },
      error: () => {
        this.errorMessage = 'Invalid credentials. Please check your domain, zip code, and phone number.';
        this.loading = false;
      },
    });
  }
}
